import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ISourcePanelApi } from '../../../services/source-panel-api/source-panel-api.interface';
import { AgentApiService } from '../../../../shared/services/agent-api/agent-api.service';
import { TokenService } from '../../../../shared/services/token/token.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { environment } from '../../../../environments/environment';
import config from '../../../../config/config.json';
import moduleInfo from '../../../../config/module_info.json';
import { RegulationNextDownloadService } from '../../../../services/regulation-next-download/regulation-next-download.service';

const REGULATIONS_AGENT_ID = 'regulations_agent';

/**
 * Wraps response to match the shape expected by the source panel (status, body).
 */
function wrapResponseForCompatibility<T>(body: T, status: number = 200): any {
  return {
    body,
    status,
    statusText: status === 200 ? 'OK' : 'Error',
    ok: status >= 200 && status < 300
  };
}

/**
 * Source panel API implementation for the regulation agent.
 * Uses the centralized agent-common metadata endpoint configured for regulations.
 */
@Injectable({
  providedIn: 'root'
})
export class RegulationAgentSourceApiService implements ISourcePanelApi {
  constructor(
    private readonly agentApiService: AgentApiService,
    private readonly tokenService: TokenService,
    private readonly loggerService: LoggerService,
    private readonly regulationNextDownloadService: RegulationNextDownloadService
  ) {}

  private get baseUrl(): string | undefined {
    return this.agentApiService.getBaseUrlForAgent(REGULATIONS_AGENT_ID);
  }

  private getEndpointPath(key: string): string {
    return this.agentApiService.getApiUrlFromConfig(key, REGULATIONS_AGENT_ID) || '';
  }

  searchMetadata(
    tenantId: string,
    searchTerm: string = '',
    chatHistoryId: string,
    relevance: string | null = null,
    sort_by: string = ''
  ): Observable<any> {
    const endpoint = this.getEndpointPath('search_metadata');
    if (!endpoint || !this.baseUrl) {
      this.loggerService.logError('[RegulationAgentSourceApi] search_metadata: missing endpoint or baseUrl', 'searchMetadata');
      return of(wrapResponseForCompatibility({ metadata: {}, relevance: false, release_date: false }, 500));
    }
    const payload = {
      tenant_id: tenantId,
      message_id: chatHistoryId,
      search_term: searchTerm,
      relevance,
      sort_by,
      sort_by_descending: true
    };
    const token = this.tokenService.getAccessToken();
    return this.agentApiService.post<any>(endpoint, payload, token, this.baseUrl).pipe(
      map(response => wrapResponseForCompatibility(response)),
      catchError(error => {
        this.loggerService.logError('[RegulationAgentSourceApi] searchMetadata error', 'searchMetadata', error);
        return of(wrapResponseForCompatibility({ metadata: {}, relevance: false, release_date: false }, 500));
      })
    );
  }

  ViewInsightFile(fileName: string, tenantId: any = 0, insight_id: any): Observable<any> {
    if (this.regulationNextDownloadService.hasGriRegulationAccess()) {
      return this.regulationNextDownloadService.getPresignedUrl(fileName);
    }
    const regulationsModule = (moduleInfo as any)?.view_access_to_regulations;
    const downloadApiToggle = regulationsModule?.DownloadApiToggle ?? (config as any)?.DownloadApiToggle ?? true;
    const riApiUrl = regulationsModule?.RIApiUrl || (config as any)?.RIApiUrl || (config as any)?.apiBaseUrl || '';
    if (!riApiUrl) {
      this.loggerService.logError('[RegulationAgentSourceApi] ViewInsightFile: RIApiUrl not configured', 'ViewInsightFile');
      return of({});
    }
    const token = this.tokenService.getAccessToken();
    if (downloadApiToggle) {
      const body = { tenantId: tenantId, fileName: fileName, sourcePath: '' };
      const customUrl = `${riApiUrl}/regulations/Regulations/v3.0/DownloadFile`;
      return this.agentApiService.post<any>(customUrl, body, token, undefined, 'text').pipe(
        catchError(error => {
          this.loggerService.logError('[RegulationAgentSourceApi] ViewInsightFile (v3) error', 'ViewInsightFile', error);
          return of({});
        })
      );
    } else {
      const params = new HttpParams()
        .set('tenantId', String(tenantId))
        .set('fileName', fileName)
        .set('insight_id', insight_id);
      const customUrl = `${riApiUrl}/gateway/Regulations/v2.0/DownloadFile`;
      return this.agentApiService.get<any>(customUrl, token, params).pipe(
        catchError(error => {
          this.loggerService.logError('[RegulationAgentSourceApi] ViewInsightFile (v2) error', 'ViewInsightFile', error);
          return of({});
        })
      );
    }
  }

  DownloadRtqFile(document_id: string): Observable<any> {
    const endpoint = this.getEndpointPath('download_rtq_file');
    if (!endpoint || !this.baseUrl) {
      this.loggerService.logError('[RegulationAgentSourceApi] DownloadRtqFile: missing endpoint or baseUrl', 'DownloadRtqFile');
      return of({});
    }
    const params = new HttpParams().set('documentId', document_id);
    const token = this.tokenService.getAccessToken();
    return this.agentApiService.get<any>(endpoint, token, params, this.baseUrl).pipe(
      catchError(error => {
        this.loggerService.logError('[RegulationAgentSourceApi] DownloadRtqFile error', 'DownloadRtqFile', error);
        return of({});
      })
    );
  }

  GetDocChatUrl(fileName: any, insight_id: any): Observable<any> {
    const regulationsModule = (moduleInfo as any)?.view_access_to_regulations;
    const riApiUrl = regulationsModule?.RIApiUrl || (config as any)?.RIApiUrl || (config as any)?.apiBaseUrl || '';
    const docChatUrl = regulationsModule?.DocChatUrl || (config as any)?.DocChatUrl || '/regulations/InsightsOS/v2.0/GetEncryptAttachmentName?fileName=';
    const tenantId = environment.TenantId || '';
    if (!riApiUrl) {
      this.loggerService.logError('[RegulationAgentSourceApi] GetDocChatUrl: RIApiUrl not configured', 'GetDocChatUrl');
      return of({});
    }
    const customUrl = `${riApiUrl}${docChatUrl}${fileName}&tenantId=${tenantId}&insight_id=${insight_id}`;
    const token = this.tokenService.getAccessToken();
    return this.agentApiService.get<any>(customUrl, token, undefined, undefined).pipe(
      catchError(error => {
        this.loggerService.logError('[RegulationAgentSourceApi] GetDocChatUrl error', 'GetDocChatUrl', error);
        return of({});
      })
    );
  }
}
